<script>
  import { createEventDispatcher } from "svelte";
  import { DoubleBounce } from "svelte-loading-spinners";
  import Select from "svelte-select/Select.svelte";
  import {
    DateInput,
    NotificationModal,
  } from "../../../Components/Common/index.svelte";

  import { apiMethods } from "../../../Services/index.svelte";
  import { user } from "../../../stores";
  import { searchGroupsFromCache } from "../../../Utils/DataLoaders.svelte";

  import { deleteIcon, editIcon } from "../../../Utils/utilConstants.svelte";

  import {
    EditRepeatingEventOptions,
    DeleteRepeatingEventOptions,
    DirectUserRoles,
  } from "../../../Utils/utilEnums.svelte";

  import {
    getBackEndDeleteType,
    showError,
  } from "../../../Utils/utilFunctions.svelte";
  import CalendarRecurrenceEditOptionModal from "../../Calendar/components/CalendarRecurrenceEditOptionModal.svelte";

  import CalendarRecurrenceModal from "../../Calendar/components/CalendarRecurrenceModal.svelte";
  import {
    resetCalendarRecurrenceEvent,
    _isAllDayEvent,
  } from "../../Calendar/helpers/calendarHelper.svelte";

  export let id,
    newValues = {},
    isModalOpen = false,
    isOpenFromCalendar = false;

  $: isCreateEvent = !accountEventObj.hasOwnProperty("id");

  $: allowGroupEdit = $user.role === DirectUserRoles.Admin;

  $: if (!isModalOpen) clearAll();

  const dispatch = createEventDispatcher();

  let isModifyingEvent = false;

  let isCalendarRecurrenceModalOpen = false;

  let isDeleteEventModalOpen = false,
    accountEventObj = {},
    prevStartDate,
    allFiles = [],
    selectedGroup,
    isLoadingEvent = false,
    isDeletingEvent = false;

  let deleteType, wasEventRepeating;

  const init = async () => {
    if (id) {
      isLoadingEvent = true;
      accountEventObj = await apiMethods.getMethod
        .getGlobalEventById(id)
        .catch((err) => {
          showError(err);
          closeModal();
        });
      allFiles = accountEventObj.eventFiles;
      if (accountEventObj.groupId && accountEventObj.groupName)
        selectedGroup = {
          id: accountEventObj.groupId,
          name: accountEventObj.groupName,
        };
      accountEventObj = {
        ...accountEventObj,
        fromTime: accountEventObj.fromDate.split("T")[1].slice(0, 5),
        toTime: accountEventObj.toDate.split("T")[1].slice(0, 5),
        isRepeating: accountEventObj.repeatIntervalType > 0,
        isFullDay: _isAllDayEvent(
          accountEventObj.fromDate,
          accountEventObj.toDate
        ),
        ...newValues,
      };
      wasEventRepeating = accountEventObj.isRepeating;
      isLoadingEvent = false;
    } else {
      accountEventObj = {
        ...newValues,
      };
    }
  };

  const closeModal = () => {
    isModalOpen = false;
    selectedGroup = null;
  };

  $: title = isLoadingEvent
    ? "Loading"
    : isCreateEvent
    ? "Create Global Event"
    : "Edit Global Event";

  const handleModifyEvent = async (e) => {
    const editType = e?.detail;
    if (isModifyingEvent) return;

    // group options update skipped as group change not allowed

    isModifyingEvent = true;

    _syncDateToCalendarEvent();

    if (!accountEventObj.isRepeating)
      accountEventObj = resetCalendarRecurrenceEvent(accountEventObj);

    await updateEvent(editType);

    isModifyingEvent = false;
  };

  const clearAll = () => {
    accountEventObj = {};
    isLoadingEvent = false;
    wasEventRepeating = false;
    prevStartDate = null;
    newValues = {};
    selectedHospital = null;
    selectedSurgeon = null;
    surgeonOptions = [];
    hospitalOptions = [];
    suggestedAccountDoctors = [];
    suggestedHospitals = [];
    accountDoctors = [];
    isCalendarRecurrenceEditOptionModalOpen = false;
    isLoadingSuggestedAccountDoctors = false;
    isLoadingSuggestedHospitals = false;
  };

  const onConfirmModifyEvent = () => {
    if (isCreateEvent) return createEvent();
    if (accountEventObj.isRepeating || wasEventRepeating) {
      return (isCalendarRecurrenceEditOptionModalOpen = true);
    } else handleModifyEvent();
  };

  const createEvent = async () => {
    if (allowGroupEdit) {
      accountEventObj.groupId = selectedGroup?.id ?? 0;
    } else {
      accountEventObj.groupId = $user.groupId ?? 0;
    }
    await apiMethods.postMethod
      .createGlobalEvent(accountEventObj)
      .then((res) => {
        if (!res || res.error)
          showError(
            res.error ??
              "Failed to create global event, please refresh the page and try again and contact support if the issue persists."
          );
        else {
          closeModal();
          dispatch("eventsChanged");
        }
      })
      .catch((err) => showError(err));
  };

  const updateEvent = async (editType) => {
    if (accountEventObj.isRepeating || wasEventRepeating) {
      switch (editType) {
        case EditRepeatingEventOptions.thisOnly:
          accountEventObj = resetCalendarRecurrenceEvent(accountEventObj);
          break;
        case EditRepeatingEventOptions.thisAndFollowing:
          accountEventObj.parentId = accountEventObj.id;
          break;
        default:
          break;
      }
    }
    await apiMethods.putMethod
      .updateGlobalEvent(accountEventObj)
      .then((res) => {
        if (!res || res.error)
          showError(
            res.error ??
              "Failed to update event, please refresh the page and try again and contact support if the issue persists."
          );
        else {
          closeModal();
          dispatch("eventsChanged");
        }
      })
      .catch((err) => showError(err));
  };

  const deleteEvent = async () => {
    isDeletingEvent = true;

    await apiMethods.deleteMethod
      .deleteGlobalEvent(accountEventObj.id, getBackEndDeleteType(deleteType))
      .then((res) => {
        if (!res || res.error)
          showError(
            res.error ??
              "Failed to delete event, please refresh the page and try again and contact support if the issue persists."
          );
        else {
          closeModal();
          dispatch("eventsChanged");
        }
      })
      .catch((err) => showError(err));

    isDeletingEvent = false;

    closeDeleteEventModal();
  };

  const openRecurrenceModalOpen = () => (isCalendarRecurrenceModalOpen = true);

  const openDeleteEventModal = () => (isDeleteEventModalOpen = true);

  const closeDeleteEventModal = () => {
    deleteType = undefined;
    isDeleteEventModalOpen = false;
  };

  const handleDateChange = (obj, isFromDate = true) => {
    if (obj.isFullDay) return obj;

    if (isFromDate && obj.fromTime) return obj;

    if (!isFromDate && obj.toTime) return obj;

    if (isFromDate) obj.fromTime = "09:00";
    else obj.toTime = "17:00";

    return obj;
  };

  const handleToggleFullDay = (obj) => {
    delete obj.fromTime;
    delete obj.toTime;

    return obj;
  };

  // HELPER FUNCTIONS
  const _syncDateToCalendarEvent = () => {
    accountEventObj.fromDate += accountEventObj.fromTime
      ? `T${accountEventObj.fromTime}:00`
      : "T00:00:00";
    accountEventObj.toDate += accountEventObj.toTime
      ? `T${accountEventObj.toTime}:59`
      : "T23:59:59";
  };

  let selectedHospital,
    selectedSurgeon,
    surgeonOptions = [],
    hospitalOptions = [],
    suggestedAccountDoctors = [],
    suggestedHospitals = [],
    accountDoctors = [],
    isCalendarRecurrenceEditOptionModalOpen = false,
    isLoadingSuggestedAccountDoctors = false,
    isLoadingSuggestedHospitals = false;
</script>

<NotificationModal
  {isModalOpen}
  {title}
  {closeModal}
  modalMaxWidth="max-w-[70vw]"
  fullWidthOnSmallerScreens
  noContentPadding
  closeModalFunction={closeModal}
  submitText={`${
    isCreateEvent
      ? isModifyingEvent
        ? "Creating"
        : "Create"
      : isModifyingEvent
      ? "Saving"
      : "Save"
  }`}
  isConfirmDisabled={!accountEventObj.fromDate || !accountEventObj.toDate}
  isConfirming={isModifyingEvent}
  confirmAction={onConfirmModifyEvent}
  headerOptions={[
    {
      iconName: "deleteIcon text-sm",
      iFont: deleteIcon,
      optionText: "Delete",
      showAsTooltip: true,
      function: () => openDeleteEventModal(),
      isHidden: !isOpenFromCalendar,
    },
  ]}
>
  <div
    class="flex flex-1 flex-col gap-y-4 overflow-y-scroll px-6 py-3"
    use:init
  >
    {#if isLoadingEvent}
      <div class="flex flex-1 my-6 items-center justify-center">
        <DoubleBounce size="32" color="#FF3E00" unit="px" duration="1s" />
      </div>
    {:else}
      <div class="flex flex-col gap-y-4 flex-1 py-3">
        {#if allowGroupEdit}
          <div class="flex flex-row justify-start gap-4 flex-wrap">
            <div class="flex gap-x-4 items-center">
              <p class="w-[120px]">Group:</p>
              {#if accountEventObj.id}
                {accountEventObj.groupName ? accountEventObj.groupName : "-"}
              {:else}
                <Select
                  bind:value={selectedGroup}
                  class="flex flex-1 rounded-full"
                  loadOptions={searchGroupsFromCache}
                  disabled={accountEventObj.id}
                  itemId="id"
                  label="name"
                  --list-z-index="3"
                  --width="268px"
                  --height="35px"
                  noOptionsMessage="No Groups Found"
                />
              {/if}
            </div>
          </div>
        {/if}

        <div class="flex flex-row justify-start gap-4 flex-wrap">
          <div class="flex gap-x-4 items-center">
            <p class="w-[120px]">From Date:</p>

            <div class="flex flex-1 gap-x-2 items-center w-[268px]">
              <DateInput
                bind:value={accountEventObj.fromDate}
                wrapperClass="input input-sm px-0 input-bordered flex-1"
                wrapperClassFocus="inputFocus"
                on:change={() =>
                  (accountEventObj = handleDateChange(accountEventObj))}
              />

              {#if !accountEventObj.isFullDay}
                <input
                  type="time"
                  bind:value={accountEventObj.fromTime}
                  class="input input-sm input-bordered"
                />
              {/if}
            </div>
          </div>

          <div class="flex gap-x-4 items-center">
            <p class="w-[120px]">To Date:</p>

            <div class="flex flex-1 gap-x-2 items-center w-[268px]">
              <DateInput
                bind:value={accountEventObj.toDate}
                wrapperClass="input input-sm px-0 input-bordered flex-1"
                wrapperClassFocus="inputFocus"
                on:change={() =>
                  (accountEventObj = handleDateChange(accountEventObj, false))}
              />

              {#if !accountEventObj.isFullDay}
                <input
                  type="time"
                  class="input input-sm input-bordered"
                  bind:value={accountEventObj.toTime}
                />
              {/if}
            </div>
          </div>
        </div>

        <div class="flex flex-row justify-start gap-4 flex-wrap">
          <div class="flex gap-x-4 items-center">
            <label class="w-[120px]" for="fullDay">Full Day:</label>

            <div class="w-[268px]">
              <input
                type="checkbox"
                id="fullDay"
                class="toggle toggle-secondary"
                bind:checked={accountEventObj.isFullDay}
                on:change={() =>
                  (accountEventObj = handleToggleFullDay(accountEventObj))}
              />
            </div>
          </div>
          <div class="flex gap-x-4 items-center">
            <p class="w-[120px]">Repeating:</p>

            <div
              class="flex flex-1 items-center gap-x-6 justify-between w-[268px]"
            >
              <input
                type="checkbox"
                class="toggle toggle-secondary"
                bind:checked={accountEventObj.isRepeating}
                on:change={() => {
                  accountEventObj.isRepeating && openRecurrenceModalOpen();
                }}
              />

              {#if accountEventObj.isRepeating}
                <div
                  class="flex items-center gap-x-2 cursor-pointer"
                  on:click={openRecurrenceModalOpen}
                >
                  <i class={editIcon} />
                  <p>Edit</p>
                </div>
              {/if}
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-1 flex-col gap-2">
        <p class="w-[120px]">Description:</p>

        <div class="flex flex-1 w-full">
          <textarea
            class="textarea textarea-bordered w-full"
            rows="6"
            autocomplete="off"
            bind:value={accountEventObj.description}
          />
        </div>
      </div>
    {/if}
  </div>
</NotificationModal>

{#if isCalendarRecurrenceModalOpen}
  <CalendarRecurrenceModal
    bind:isModalOpen={isCalendarRecurrenceModalOpen}
    bind:eventObj={accountEventObj}
  />
{/if}

<NotificationModal
  isModalOpen={isDeleteEventModalOpen}
  title="Delete Event"
  submitText="Cancel"
  confirmButtonClass="btn-accent"
  cancelText={isDeletingEvent ? "Deleting" : "Delete"}
  isConfirming={isDeletingEvent}
  confirmAction={closeDeleteEventModal}
  closeModal={deleteEvent}
  isCancelDisabled={!deleteType &&
    (accountEventObj.isRepeating || wasEventRepeating)}
  isReverseBtns={true}
  zIndex="1001"
  closeModalFunction={closeDeleteEventModal}
>
  {#if accountEventObj.isRepeating || wasEventRepeating}
    <p class="leading-6 whitespace-pre-line mb-4">Do you want to delete:</p>
    <div class="flex flex-col gap-y-4 font-medium">
      <label class="flex items-center gap-4">
        <input
          type="radio"
          class="radio radio-secondary"
          value={DeleteRepeatingEventOptions.thisOnly}
          bind:group={deleteType}
        />
        This event only
      </label>
      <label class="flex items-center gap-4">
        <input
          type="radio"
          class="radio radio-secondary"
          value={DeleteRepeatingEventOptions.thisAndFollowing}
          bind:group={deleteType}
        />
        This and following events
      </label>
      <label class="flex items-center gap-4">
        <input
          type="radio"
          class="radio radio-secondary"
          value={DeleteRepeatingEventOptions.allEvents}
          bind:group={deleteType}
        />
        All events
      </label>
    </div>
  {:else}
    Do you want to delete this event?
  {/if}
</NotificationModal>

{#if isCalendarRecurrenceEditOptionModalOpen}
  <CalendarRecurrenceEditOptionModal
    {accountEventObj}
    bind:isModalOpen={isCalendarRecurrenceEditOptionModalOpen}
    on:proceedAction={handleModifyEvent}
    {wasEventRepeating}
  />
{/if}
